import { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../hooks/useApi';
import { useApp } from '../../context';
import { parseRoles } from '../../utils/rbac';
import { formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';
import RaidQuickPanel from './RaidQuickPanel';
import ActionsPanel from '../shared/ActionsPanel';
import { MILESTONE_STATUS_TRANSITIONS, isMilestoneDone, sesNumberRequired } from '../../utils/milestoneStatus';

// ── Constants ───────────────────────────────────────────────────────────────
const RAG_OPTS = [
  { value: 'green', label: 'Green', dot: '#10B981', bg: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'amber', label: 'Amber', dot: '#F59E0B', bg: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'red',   label: 'Red',   dot: '#EF4444', bg: 'bg-red-100 text-red-700 border-red-300' },
];
const RAG_DOT = { green: '#10B981', amber: '#F59E0B', red: '#EF4444' };
// Schedule RAG is derived from delivery status; overall = worst of the four.
const STATUS_RAG = { on_track: 'green', at_risk: 'amber', delayed: 'red', blocked: 'red', on_hold: 'amber', uat: 'green', complete: 'green' };
const ragFromDelivery = (s) => STATUS_RAG[s] || 'green';
const worstRag = (...rags) => rags.includes('red') ? 'red' : rags.includes('amber') ? 'amber' : 'green';

const DELIVERY_STATUSES = [
  { value: 'on_track', label: 'On Track', bg: 'bg-emerald-100 text-emerald-700 border-emerald-300', dot: '#10B981' },
  { value: 'at_risk',  label: 'At Risk',  bg: 'bg-amber-100 text-amber-700 border-amber-300',       dot: '#F59E0B' },
  { value: 'delayed',  label: 'Delayed',  bg: 'bg-red-100 text-red-700 border-red-300',             dot: '#EF4444' },
  { value: 'blocked',  label: 'Blocked',  bg: 'bg-red-100 text-red-700 border-red-300',             dot: '#EF4444' },
  { value: 'on_hold',  label: 'On Hold',  bg: 'bg-gray-100 text-gray-600 border-gray-300',          dot: '#9CA3AF' },
  { value: 'uat',      label: 'UAT',      bg: 'bg-purple-100 text-purple-700 border-purple-300',    dot: '#8B5CF6' },
  { value: 'complete', label: 'Complete', bg: 'bg-teal-100 text-teal-700 border-teal-300',          dot: '#14B8A6' },
];
const dsMeta = v => DELIVERY_STATUSES.find(s => s.value === v) || DELIVERY_STATUSES[0];

// Terminal/finance statuses stay read-only; everything else follows the
// shared MILESTONE_STATUS_TRANSITIONS map (same rules as the SOW Milestones tab).
const TERMINAL = ['Invoiced', 'Paid', 'Complete', 'Completed', 'Credit Noted'];
// "Done as far as the PM is concerned" — delivered and just awaiting finance — is
// isMilestoneDone() in utils/milestoneStatus. Those rows collapse to a compact
// read-only row (no summary/dependency/etc).
const todayStr = () => new Date().toISOString().split('T')[0];

export default function ProjectUpdatePage() {
  const { toast } = useToast();
  const { currentUser, detailId, registerNavGuard } = useApp();
  // Baseline (scheduled/contract) dates are governed — only PMO/admin can change
  // them (normally via a CCN). PMs manage the forecast date only.
  const isPmo = useMemo(() => {
    const roles = parseRoles(currentUser?.user_role);
    return roles.includes('pmo') || roles.includes('admin');
  }, [currentUser]);
  const [view, setView]         = useState('mine');
  const [viewDefaulted, setViewDefaulted] = useState(false);
  const [pmFilter, setPmFilter] = useState('all');
  const [search, setSearch]     = useState('');
  const editorRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  // Unsaved-changes guard: PMs kept forgetting to press Save and losing edits
  // when switching project or closing the tab. Rather than track edits with a
  // flag (which mis-fired on the programmatic load and falsely reported unsaved
  // changes on open), we snapshot the form at load time and derive `dirty` by
  // comparing the live form against that baseline. Opening a project can never
  // be "dirty" because the snapshot IS the loaded state.
  const baselineRef = useRef(null);

  // Narrative form
  const [narr, setNarr] = useState({ delivery_status: 'on_track', budget_rag: 'green', resource_rag: 'green', risk_rag: 'green', recent_progress: '', next_period: '', rr_actions: '', rr_comment: '' });
  // Per-milestone edits, keyed by milestone id
  const [edits, setEdits] = useState({});

  // Config dropdowns
  const [missedReasons, setMissedReasons] = useState([]);
  const [depReasons, setDepReasons]       = useState([]);
  // People — for the actions panel (@mention assignees / owner display).
  const [people, setPeople] = useState([]);

  useEffect(() => {
    api.get('config-options', { category: 'missed_milestone_reason' })
      .then(d => setMissedReasons(Array.isArray(d) ? d.filter(x => x.is_active !== 0) : [])).catch(() => {});
    api.get('config-options', { category: 'dependency_reason' })
      .then(d => setDepReasons(Array.isArray(d) ? d.filter(x => x.is_active !== 0) : [])).catch(() => {});
    api.get('people').then(d => setPeople(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // Several list requests can be in flight at once on a fresh load (the mount
  // fetch for "mine", StrictMode's re-run of it, and the PMO default switching
  // to "all"), and whichever resolved last used to own the list — so a stale,
  // often empty, "mine" response could overwrite the "all" list after the tab
  // had already moved on. Only the latest request may touch state.
  const listReqRef = useRef(0);
  const loadList = () => {
    const reqId = ++listReqRef.current;
    setLoading(true);
    api.get('project-updates', { view })
      .then(r => {
        if (reqId !== listReqRef.current) return;
        setProjects(Array.isArray(r) ? r : []);
        setLoading(false);
      })
      .catch(() => {
        if (reqId !== listReqRef.current) return;
        setProjects([]);
        setLoading(false);
      });
  };
  useEffect(loadList, [view]);

  const openProject = (sowId) => {
    // Guard against silently discarding unsaved edits when switching project.
    if (dirty && sowId !== selected && !window.confirm('You have unsaved changes to this project update.\n\nLeave without saving? Your edits will be lost.')) return;
    setSelected(sowId);
    baselineRef.current = null; // no baseline until the load completes → not dirty
    setDetailLoading(true);
    setDetail(null);
    setShowAllHistory(false);
    // Bring the editor into view — otherwise, selecting a row far down the list
    // leaves the panel's top scrolled off the top of the viewport.
    requestAnimationFrame(() => editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    api.get(`project-updates/${sowId}`).then(d => {
      setDetail(d);
      const l = d.latest || {};
      const nextNarr = {
        delivery_status: l.delivery_status || 'on_track',
        budget_rag: l.budget_rag || 'green',
        resource_rag: l.resource_rag || 'green',
        risk_rag: l.risk_rag || 'green',
        recent_progress: l.recent_progress || '',
        next_period: l.next_period || '',
        rr_actions: d.sow?.rr_actions || '',
        rr_comment: d.sow?.rr_comment || '',
      };
      setNarr(nextNarr);
      const e = {};
      for (const m of (d.milestones || [])) {
        e[m.id] = {
          contract_date: m.contract_date || '',
          forecast_date: m.forecast_date || '',
          status: m.status || 'Awaiting',
          summary: m.summary || '',
          missed_milestone_reason: m.missed_milestone_reason || '',
          dependency_reason: m.dependency_reason || '',
          dependency_comment: m.dependency_comment || '',
          ses_number: m.ses_number || '',
          evidence_title: '',
          evidence_url: '',
          _orig: { contract_date: m.contract_date || '', forecast_date: m.forecast_date || '', status: m.status || 'Awaiting' },
        };
      }
      setEdits(e);
      // Snapshot the loaded form. `dirty` compares the live form to this, so the
      // freshly-loaded state is by definition clean.
      baselineRef.current = JSON.stringify({ narr: nextNarr, edits: e });
      setDetailLoading(false);
    }).catch(() => { setDetailLoading(false); });
  };

  // Derive dirty by comparing the live form to the load-time snapshot. Immune
  // to render timing — opening a project is never falsely flagged as unsaved.
  const dirty = useMemo(
    () => !!baselineRef.current && JSON.stringify({ narr, edits }) !== baselineRef.current,
    [narr, edits],
  );

  // Warn before closing/reloading the tab with unsaved edits.
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  // Guard in-app navigation (sidebar / links) too: block leaving the page with
  // unsaved edits unless the PM confirms. Cleared on unmount.
  useEffect(() => {
    if (!registerNavGuard) return;
    registerNavGuard(() => !dirty || window.confirm('You have unsaved changes to this project update.\n\nLeave without saving? Your edits will be lost.'));
    return () => registerNavGuard(null);
  }, [dirty, registerNavGuard]);

  // PMO/admin default to the "All Active" tab (they report across the whole
  // portfolio, not just their own projects); PMs stay on "My Projects". Runs
  // once the user has loaded and never overrides a manual tab change afterwards.
  useEffect(() => {
    if (viewDefaulted || !currentUser) return;
    setViewDefaulted(true);
    if (isPmo) setView('all');
  }, [currentUser, isPmo, viewDefaulted]);

  // Deep-link: open a specific project when navigated here with a SOW id (e.g.
  // from the dashboard's missed-milestone alert). Falls back to "All Active" if
  // the project isn't in "My Projects" (a missed milestone can belong to a
  // project the current user doesn't own/PM).
  const [deepLinked, setDeepLinked] = useState(false);
  useEffect(() => {
    if (deepLinked || !detailId || loading || projects.length === 0) return;
    const p = projects.find(x => x.id === detailId);
    if (!p) {
      // Not in the active list. Widen to "All Active" first; if it's still not
      // there (e.g. a Completed SOW opened from its detail page), open it
      // directly — the detail endpoint serves any SOW regardless of the list.
      if (view !== 'all') { setView('all'); return; }
      setDeepLinked(true);
      openProject(detailId);
      return;
    }
    setDeepLinked(true);
    openProject(p.id);
  }, [detailId, projects, loading, deepLinked, view]); // eslint-disable-line react-hooks/exhaustive-deps

  const setMs = (id, patch) => setEdits(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const isMissed = (m, e) => {
    const f = e.forecast_date;
    // Judged on the status being edited: marking a late milestone Ready to bill in this
    // sitting settles it, so there is no missed-milestone reason left to give.
    return f && f < todayStr() && !isMilestoneDone(e.status);
  };
  const contractChanged = (e) => (e.contract_date || '') !== (e._orig.contract_date || '');

  const save = async () => {
    if (!detail) return;
    const sesApplicable = !!detail.sow?.ses_applicable;
    // ── Validate ──
    for (const m of detail.milestones) {
      const e = edits[m.id]; if (!e) continue;
      if (isMissed(m, e) && !e.missed_milestone_reason) {
        toast.error(`${m.milestone_code} is past its forecast date — select a missed-milestone reason.`); return;
      }
      if (contractChanged(e) && (!e.evidence_title.trim() || !e.evidence_url.trim())) {
        toast.error(`${m.milestone_code}: add evidence (title + link) for the contract date change.`); return;
      }
      if (sesNumberRequired({ sesApplicable, savedStatus: e._orig.status, newStatus: e.status }) && !e.ses_number.trim()) {
        toast.error(`${m.milestone_code}: a SES number is required to mark Ready to Bill.`); return;
      }
    }

    // Project-health rule: an At Risk or Blocked project should have a risk or
    // issue on the RAID register. This LEVERAGES existing RAID data — any open
    // risk/issue already on the project satisfies it, so we only block when the
    // project has nothing logged at all (no need to raise a fresh one each time).
    if (narr.delivery_status === 'at_risk' || narr.delivery_status === 'blocked') {
      let raid = [];
      try { raid = await api.get('risks', { sow_id: selected }) || []; } catch { raid = []; }
      // Count any non-closed risk/issue — i.e. exactly what shows in the RAID
      // panel, so if the user can see one there, it satisfies this.
      const hasRiskOrIssue = raid.some(x => x.status !== 'closed' && ['risk', 'issue'].includes(x.raid_type || 'risk'));
      if (!hasRiskOrIssue) {
        const kind = narr.delivery_status === 'blocked' ? 'issue' : 'risk';
        toast.error(`Status is ${narr.delivery_status === 'blocked' ? 'Blocked' : 'At Risk'} — log a ${kind} in the RAID panel below (or link an existing one) before saving.`);
        return;
      }
    }

    setSaving(true);
    try {
      // 1) Project narrative
      await api.post(`project-updates/${selected}`, {
        delivery_status: narr.delivery_status,
        overall_rag: worstRag(ragFromDelivery(narr.delivery_status), narr.budget_rag, narr.resource_rag, narr.risk_rag),
        budget_rag: narr.budget_rag, resource_rag: narr.resource_rag, risk_rag: narr.risk_rag,
        recent_progress: narr.recent_progress || null,
        next_period: narr.next_period || null,
        week_commencing: todayStr(),
      });

      // 1b) RR actions/comment live on the SOW itself (project level, not the RAG snapshot)
      if ((narr.rr_actions || '') !== (detail.sow?.rr_actions || '') || (narr.rr_comment || '') !== (detail.sow?.rr_comment || '')) {
        await api.put(`sows/${selected}`, { rr_actions: narr.rr_actions || null, rr_comment: narr.rr_comment || null });
      }

      // 2) Milestone changes via the existing milestone endpoint (keeps all side-effects)
      for (const m of detail.milestones) {
        const e = edits[m.id]; if (!e) continue;
        const payload = {};
        if ((e.contract_date || '') !== (e._orig.contract_date || '')) payload.contract_date = e.contract_date || null;
        if ((e.forecast_date || '') !== (e._orig.forecast_date || '')) payload.forecast_date = e.forecast_date || null;
        if (e.status !== e._orig.status) payload.status = e.status;
        if ((e.summary || '') !== (m.summary || '')) payload.summary = e.summary || null;
        if ((e.dependency_reason || '') !== (m.dependency_reason || '')) payload.dependency_reason = e.dependency_reason || null;
        if ((e.dependency_comment || '') !== (m.dependency_comment || '')) payload.dependency_comment = e.dependency_comment || null;
        if ((e.missed_milestone_reason || '') !== (m.missed_milestone_reason || '')) payload.missed_milestone_reason = e.missed_milestone_reason || null;
        if (e.ses_number.trim() && e.ses_number.trim() !== (m.ses_number || '')) payload.ses_number = e.ses_number.trim();
        if (Object.keys(payload).length) {
          await api.put(`milestones/${m.id}`, payload);
        }
        if (contractChanged(e)) {
          await api.post(`milestones/${m.id}/documents`, {
            document_type: 'contract_date_change',
            title: e.evidence_title,
            url: e.evidence_url,
            notes: `Contract date changed to ${e.contract_date} via project update`,
          });
        }
      }

      toast.success('Project update saved');
      loadList();
      openProject(selected); // re-snapshots the baseline → clean again
    } catch (err) {
      toast.error('Failed to save: ' + (err.message || 'error'));
    } finally {
      setSaving(false);
    }
  };

  // Search by AV reference, project name (detail) or client.
  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q ? projects : projects.filter(p =>
      (p.reference || '').toLowerCase().includes(q) ||
      (p.detail || '').toLowerCase().includes(q) ||
      (p.client_name || '').toLowerCase().includes(q));
    // Chunk the delivered/awaiting-finance projects to the bottom — nothing to do there.
    return [...base].sort((a, b) => (a.awaiting_finance ? 1 : 0) - (b.awaiting_finance ? 1 : 0));
  }, [projects, search]);

  // A project falls under its PM (the SOW's Project Manager, owner_id) — PMs
  // write the updates, so that's whose group it sits in and who the "due" count
  // chases. The delivery owner is only a fallback when no PM is set.
  const pmOf = (p) => p.owner_name || p.delivery_owner_name || 'Unassigned';
  // For "All Active", group projects by their PM with a count of how many are
  // due an update, so PMO can chase per person.
  const groups = useMemo(() => {
    if (view !== 'all') return null;
    const map = new Map();
    for (const p of filteredProjects) {
      const key = pmOf(p);
      if (!map.has(key)) map.set(key, { pm: key, projects: [], due: 0 });
      const g = map.get(key);
      g.projects.push(p);
      if (p.rag_stale) g.due += 1;
    }
    return Array.from(map.values()).sort((a, b) => (b.due - a.due) || a.pm.localeCompare(b.pm));
  }, [filteredProjects, view]);

  const visibleGroups = useMemo(() => {
    if (!groups) return null;
    return pmFilter === 'all' ? groups : groups.filter(g => g.pm === pmFilter);
  }, [groups, pmFilter]);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">Project Update</h2>
          <div className="text-[9px] text-gray-500">One update per project drives RAG, delivery status and commentary — milestones below carry their own dates, status and summary.</div>
        </div>
        <div className="flex gap-1">
          {[['mine', 'My Projects'], ['all', 'All Active']].map(([v, l]) => (
            <button key={v} onClick={() => { setView(v); setPmFilter('all'); setSelected(null); setDetail(null); }}
              className={`px-3 py-1 text-[10px] font-semibold rounded border ${view === v ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>{l}</button>
          ))}
        </div>
      </div>

      {/* PM filter — lets PMO/leadership jump straight to one PM's projects */}
      {view === 'all' && groups && groups.length > 1 && (
        <div className="flex items-center gap-1 mb-3 flex-wrap">
          <span className="text-[8px] font-bold text-gray-400 uppercase mr-1">PM:</span>
          <button onClick={() => setPmFilter('all')}
            className={`px-2 py-0.5 text-[9px] font-semibold rounded border ${pmFilter === 'all' ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>All</button>
          {groups.map(g => (
            <button key={g.pm} onClick={() => setPmFilter(g.pm)}
              className={`px-2 py-0.5 text-[9px] font-semibold rounded border ${pmFilter === g.pm ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
              {g.pm}{g.due > 0 ? ` (${g.due})` : ''}
            </button>
          ))}
        </div>
      )}

      {/* Search — by AV number, project name or client */}
      <div className="mb-3">
        <div className="relative sm:max-w-xs">
          <svg className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-300" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search AV number, project or client…"
            className="w-full form-input text-[11px] pl-7" />
          {search && (
            <button onClick={() => setSearch('')} title="Clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 text-xs">✕</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 items-start">
        {/* Project list — pinned and independently scrollable on wide screens, so
            reading/scrolling a project's detail on the right never moves the list
            and you don't lose your place. */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden self-start lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
          {loading && <div className="text-center py-8 text-gray-400 text-xs">Loading…</div>}
          {!loading && projects.length === 0 && <div className="text-center py-8 text-gray-400 text-xs">No active projects.</div>}
          {!loading && projects.length > 0 && view === 'all' && visibleGroups?.length === 0 && <div className="text-center py-8 text-gray-400 text-xs">{search.trim() ? 'No projects match your search.' : 'No projects for this PM.'}</div>}
          {!loading && projects.length > 0 && view === 'mine' && filteredProjects.length === 0 && <div className="text-center py-8 text-gray-400 text-xs">No projects match your search.</div>}
          {/* Flat list (My Projects) */}
          {view === 'mine' && filteredProjects.map(p => <ProjectRow key={p.id} p={p} selected={selected} onOpen={openProject} />)}
          {/* Grouped by PM (All Active) */}
          {view === 'all' && visibleGroups && visibleGroups.map(g => (
            <div key={g.pm}>
              <div className="flex items-center justify-between px-3 py-1.5 bg-gray-50 border-b border-gray-100 sticky top-0">
                <span className="text-[10px] font-bold text-navy truncate">{g.pm}</span>
                {g.due > 0
                  ? <span className="text-[8px] bg-amber-500 text-amber-950 px-1.5 py-0.5 rounded-full font-bold" title="Projects due an update">{g.due} due</span>
                  : <span className="text-[8px] text-emerald-600 font-semibold">up to date</span>}
              </div>
              {g.projects.map(p => <ProjectRow key={p.id} p={p} selected={selected} onOpen={openProject} />)}
            </div>
          ))}
        </div>

        {/* Editor */}
        <div className="min-w-0 scroll-mt-4" ref={editorRef}>
          {!selected && <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400 text-xs">Select a project to give an update.</div>}
          {selected && detailLoading && <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-400 text-xs">Loading…</div>}
          {selected && detail && !detailLoading && (
            <div className="space-y-4">
              {/* Header */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-extrabold text-navy">{detail.sow.reference}</div>
                    <div className="text-[10px] text-gray-500">{detail.sow.detail}{detail.sow.client_name ? ` · ${detail.sow.client_name}` : ''}{detail.sow.po_number ? ` · PO ${detail.sow.po_number}` : ''}</div>
                  </div>
                  {detail.latest && (
                    <div className="text-[8px] text-gray-400 text-right shrink-0">
                      Last update {detail.latest.week_commencing ? formatDate(detail.latest.week_commencing) : '—'}<br/>
                      {detail.latest.updated_by_name || ''}
                    </div>
                  )}
                </div>
              </div>

              {/* Project narrative */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                <div className="text-[10px] font-bold text-gray-400 uppercase">Project Update</div>

                <div>
                  <label className="block text-[9px] font-bold text-gray-500 uppercase mb-1">Delivery Status</label>
                  <div className="flex flex-wrap gap-1.5">
                    {DELIVERY_STATUSES.map(s => (
                      <button key={s.value} onClick={() => setNarr(n => ({ ...n, delivery_status: s.value }))}
                        className={`text-[9px] px-2.5 py-1 rounded border font-semibold ${narr.delivery_status === s.value ? s.bg : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>{s.label}</button>
                    ))}
                  </div>
                </div>

                <div>
                  {(() => {
                    const scheduleRag = ragFromDelivery(narr.delivery_status);
                    const overall = worstRag(scheduleRag, narr.budget_rag, narr.resource_rag, narr.risk_rag);
                    const oR = RAG_OPTS.find(o => o.value === overall) || RAG_OPTS[0];
                    const sR = RAG_OPTS.find(o => o.value === scheduleRag) || RAG_OPTS[0];
                    return (
                      <>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-[9px] font-bold text-gray-500 uppercase">RAG status</label>
                          <span className="text-[8px] text-gray-500 inline-flex items-center gap-1">Overall
                            <span className={`px-2 py-0.5 rounded border font-semibold inline-flex items-center gap-1 ${oR.bg}`}>
                              <span className="inline-block w-2 h-2 rounded-full" style={{ background: oR.dot }} />{oR.label}
                            </span>
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div>
                            <span className="text-[8px] font-semibold text-gray-400 block mb-0.5">Schedule (auto)</span>
                            <span className={`w-full text-[9px] px-2 py-1 rounded border font-semibold inline-flex items-center justify-center gap-1 ${sR.bg}`} title="Set automatically from delivery status">
                              <span className="inline-block w-2 h-2 rounded-full" style={{ background: sR.dot }} />{sR.label}
                            </span>
                          </div>
                          {[['Budget', 'budget_rag'], ['Resource', 'resource_rag'], ['Risk', 'risk_rag']].map(([label, key]) => (
                            <div key={key}>
                              <span className="text-[8px] font-semibold text-gray-400 block mb-0.5">{label}</span>
                              <div className="flex gap-0.5">
                                {RAG_OPTS.map(r => (
                                  <button key={r.value} type="button" onClick={() => setNarr(n => ({ ...n, [key]: r.value }))}
                                    title={r.label}
                                    className={`flex-1 px-1 py-1 rounded border inline-flex items-center justify-center ${narr[key] === r.value ? r.bg : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: r.dot }} />
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>

                {[['recent_progress', 'Recent progress', 'What was achieved this period'],
                  ['next_period', 'Next period', "What's planned next"]].map(([k, label, ph]) => (
                  <div key={k}>
                    <label className="block text-[9px] font-bold text-gray-500 uppercase mb-1">{label}</label>
                    <textarea rows={4} className="form-input text-[11px] resize-y" placeholder={ph}
                      value={narr[k]} onChange={e => setNarr(n => ({ ...n, [k]: e.target.value }))} />
                  </div>
                ))}
                {/* RR (customer) fields — project level, saved onto the SOW */}
                <div className="pt-2 mt-1 border-t border-gray-100 grid gap-3">
                  <div className="text-[8px] font-bold text-gray-400 uppercase -mb-1">RR (customer) — project level</div>
                  {[['rr_actions', 'RR actions', 'Actions owned by RR / the customer'],
                    ['rr_comment', 'RR comment', 'RR / customer commentary']].map(([k, label, ph]) => (
                    <div key={k}>
                      <label className="block text-[9px] font-bold text-gray-500 uppercase mb-1">{label}</label>
                      <textarea rows={4} className="form-input text-[11px] resize-y" placeholder={ph}
                        value={narr[k]} onChange={e => setNarr(n => ({ ...n, [k]: e.target.value }))} />
                    </div>
                  ))}
                </div>
              </div>

              {/* RAID — quick capture, with a link to the full register for this
                  project. The register opens in a new browser tab so this update
                  isn't lost; when the PM returns, the panel below re-fetches so
                  any changes they made there show straight away. */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold text-gray-400 uppercase">RAID (this project)</span>
                  <a
                    href={`${window.location.pathname}#risks/${selected}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-[9px] font-semibold text-navy hover:underline whitespace-nowrap"
                    title="Open the full RAID register for this project in a new tab"
                  >
                    Open full RAID register ↗
                  </a>
                </div>
                <RaidQuickPanel sowId={selected} title="" />
              </div>

              {/* Actions — active items on this project, plus raise a new one.
                  Active shown by default with a link to reveal completed ones. */}
              <div>
                <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">Actions</div>
                <ActionsPanel
                  entityType="sow" entityId={selected}
                  people={people} milestones={detail.milestones} currentUser={currentUser}
                />
              </div>

              {/* Milestone overview */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">Milestones</div>
                <div className="text-[8px] text-gray-400 mb-3">Forecast and contract dates feed the missed-milestone and dependency logic — keep them accurate.</div>
                {detail.milestones.length === 0 && <div className="text-[10px] text-gray-400">No active milestones on this project.</div>}
                <div className="space-y-3">
                  {detail.milestones.map(m => {
                    const e = edits[m.id]; if (!e) return null;
                    const terminal = TERMINAL.includes(e.status);
                    const missed = isMissed(m, e);
                    const statusOpts = MILESTONE_STATUS_TRANSITIONS[e._orig?.status] || [e._orig?.status || e.status];
                    const sesWanted = sesNumberRequired({ sesApplicable: detail.sow.ses_applicable, savedStatus: e._orig?.status, newStatus: e.status });

                    // Delivered and just awaiting finance (ready to bill / invoiced /
                    // paid) — collapse to a compact read-only row; no summary,
                    // dependency or date fields needed.
                    //
                    // Judged on the SAVED status, never the one being edited. Collapsing on
                    // the edit closed the row the moment the PM chose Ready to bill, taking
                    // the status dropdown and the SES number field with it and leaving a
                    // save that could not be satisfied on this screen.
                    const collapsed = isMilestoneDone(e._orig?.status);
                    if (collapsed) {
                      return (
                        <div key={m.id} className="border border-gray-200 rounded-lg px-3 py-2 bg-gray-50/60 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <span className="text-[10px] font-bold text-navy">{m.milestone_code}</span>
                            <span className="text-[9px] text-gray-500 ml-2">{m.description}</span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0 text-[9px]">
                            {e.forecast_date && <span className="text-gray-400">{formatDate(e.forecast_date)}</span>}
                            <span className="px-1.5 py-0.5 rounded font-semibold bg-emerald-100 text-emerald-700">{e.status}</span>
                            {m.invoice_amount_excl != null && <span className="text-gray-600 font-semibold">£{Number(m.invoice_amount_excl).toLocaleString()}</span>}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={m.id} className={`border rounded-lg p-3 ${missed ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <span className="text-[10px] font-bold text-navy">{m.milestone_code}</span>
                            <span className="text-[9px] text-gray-500 ml-2">{m.description}</span>
                          </div>
                          {missed && <span className="text-[7px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded font-bold shrink-0">PAST FORECAST</span>}
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <div>
                            <label className="block text-[8px] text-gray-500 mb-0.5">Contract Date {!isPmo && <span className="text-gray-300 normal-case">· PMO</span>}</label>
                            {isPmo ? (
                              <input type="date" className="form-input text-[9px] py-1" value={e.contract_date}
                                onChange={ev => setMs(m.id, { contract_date: ev.target.value })} />
                            ) : (
                              <div className="text-[9px] py-1 text-gray-600" title="Contract date — changed by PMO via a CCN">{e.contract_date ? formatDate(e.contract_date) : '—'}</div>
                            )}
                          </div>
                          <div>
                            <label className="block text-[8px] text-gray-500 mb-0.5">Forecast</label>
                            <input type="date" className="form-input text-[9px] py-1" value={e.forecast_date}
                              onChange={ev => setMs(m.id, { forecast_date: ev.target.value })} />
                          </div>
                          <div>
                            <label className="block text-[8px] text-gray-500 mb-0.5">Status</label>
                            {terminal ? (
                              <div className="text-[9px] py-1 font-semibold text-gray-600">{e.status}</div>
                            ) : (
                              <select className="form-input text-[9px] py-1" value={e.status} onChange={ev => setMs(m.id, { status: ev.target.value })}>
                                {statusOpts.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            )}
                          </div>
                          <div>
                            <label className="block text-[8px] text-gray-500 mb-0.5">Invoice £</label>
                            <div className="text-[9px] py-1 text-gray-500">{m.invoice_amount_excl != null ? `£${Number(m.invoice_amount_excl).toLocaleString()}` : '—'}</div>
                          </div>
                        </div>

                        <div className="mt-2">
                          <label className="block text-[8px] text-gray-500 mb-0.5">Summary</label>
                          <input className="form-input text-[9px] py-1" placeholder="Short status for this milestone…"
                            value={e.summary} onChange={ev => setMs(m.id, { summary: ev.target.value })} />
                        </div>

                        {/* Missed milestone reason — required when past forecast */}
                        {missed && (
                          <div className="mt-2">
                            <label className="block text-[8px] font-bold text-amber-700 mb-0.5">Missed milestone reason *</label>
                            <select className="form-input text-[9px] py-1" value={e.missed_milestone_reason}
                              onChange={ev => setMs(m.id, { missed_milestone_reason: ev.target.value })}>
                              <option value="">— select reason —</option>
                              {missedReasons.map(r => <option key={r.id} value={r.value}>{r.label}</option>)}
                            </select>
                            {missedReasons.length === 0 && <div className="text-[7px] text-gray-400 mt-0.5">No reasons configured — ask an admin to add them in Settings.</div>}
                          </div>
                        )}

                        {/* Dependency (optional, attached to this milestone) */}
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[8px] text-gray-500 mb-0.5">Dependency / blocker</label>
                            <select className="form-input text-[9px] py-1" value={e.dependency_reason}
                              onChange={ev => setMs(m.id, { dependency_reason: ev.target.value })}>
                              <option value="">— none —</option>
                              {depReasons.map(r => <option key={r.id} value={r.value}>{r.label}</option>)}
                            </select>
                          </div>
                          {e.dependency_reason && (
                            <div>
                              <label className="block text-[8px] text-gray-500 mb-0.5">Dependency detail</label>
                              <input className="form-input text-[9px] py-1" placeholder={e.dependency_reason === '3rd Party' ? 'Name the third party and what it\'s waiting on' : "What's it waiting on?"}
                                value={e.dependency_comment} onChange={ev => setMs(m.id, { dependency_comment: ev.target.value })} />
                            </div>
                          )}
                        </div>

                        {/* Evidence — required when contract date changed */}
                        {contractChanged(e) && (
                          <div className="mt-2 bg-blue-50 border border-blue-200 rounded p-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div className="sm:col-span-2 text-[8px] font-bold text-blue-700">Contract date changed — evidence required *</div>
                            <input className="form-input text-[9px] py-1" placeholder="Evidence title (e.g. CCN-12)"
                              value={e.evidence_title} onChange={ev => setMs(m.id, { evidence_title: ev.target.value })} />
                            <input className="form-input text-[9px] py-1" placeholder="Link / document path"
                              value={e.evidence_url} onChange={ev => setMs(m.id, { evidence_url: ev.target.value })} />
                          </div>
                        )}

                        {/* SES number — required to mark Ready to Bill when SES applies.
                            Collected here rather than sending the PM to the SOW's
                            Milestones tab to do the same thing. */}
                        {sesWanted && (
                          <div className="mt-2 bg-purple-50 border border-purple-200 rounded p-2">
                            <label className="block text-[8px] font-bold text-purple-700 mb-0.5">Ready to Bill — SES number required *</label>
                            <input className="form-input text-[9px] py-1" placeholder="Service Entry Sheet number"
                              value={e.ses_number} onChange={ev => setMs(m.id, { ses_number: ev.target.value })} />
                            <div className="text-[7px] text-purple-600 mt-1">This SOW is SES applicable, so the number is recorded with the milestone before it can be billed.</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Save */}
              <div className="flex justify-end items-center gap-2 sticky bottom-0 bg-gray-50 py-3">
                {dirty && !saving && (
                  <span className="text-[10px] font-semibold text-amber-600 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Unsaved changes
                  </span>
                )}
                <button onClick={save} disabled={saving}
                  className={`text-white text-[11px] font-bold px-5 py-2 rounded disabled:opacity-40 transition-colors ${dirty && !saving ? 'bg-amber-500 hover:bg-amber-600 ring-2 ring-amber-300' : 'bg-navy hover:bg-navy/90'}`}>
                  {saving ? 'Saving…' : dirty ? 'Save Project Update *' : 'Save Project Update'}
                </button>
              </div>

              {/* Recent updates */}
              {detail.history && detail.history.length > 1 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="text-[10px] font-bold text-gray-400 uppercase mb-2">Recent updates</div>
                  <div className="text-[8px] text-gray-400 mb-2">Each entry shows what changed since the previous update — click one to see the full detail.</div>
                  <div className="space-y-1.5">
                    {/* history is newest-first, so the "previous" snapshot is the next row down */}
                    {(showAllHistory ? detail.history : detail.history.slice(0, 6)).map((h, i) => (
                      <UpdateHistoryRow key={h.id} h={h} prev={detail.history[i + 1] || null} />
                    ))}
                  </div>
                  {detail.history.length > 6 && (
                    <button onClick={() => setShowAllHistory(v => !v)}
                      className="mt-2 text-[9px] font-semibold text-navy hover:underline">
                      {showAllHistory ? 'Show fewer' : `Show all ${detail.history.length} updates`}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Project list row ──────────────────────────────────────────────────────────
function ProjectRow({ p, selected, onOpen }) {
  const ds = dsMeta(p.delivery_status);
  // Delivered and just awaiting payment — nothing more for the PM to do, so mute
  // the row and drop the delivery-status call-out (it's finance catching up).
  const awaiting = !!p.awaiting_finance;
  return (
    <button onClick={() => onOpen(p.id)}
      className={`w-full text-left px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50 ${selected === p.id ? 'bg-navy/5 border-l-2 border-l-navy' : awaiting ? 'bg-gray-50/60 opacity-70' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-[11px] font-bold truncate ${awaiting ? 'text-gray-500' : 'text-navy'}`}>{p.reference}</span>
        <span className="inline-flex items-center gap-1">
          {!awaiting && p.overall_rag && <span className="inline-block w-2 h-2 rounded-full" style={{ background: RAG_DOT[p.overall_rag] }} />}
          {awaiting
            ? <span className="text-[7px] bg-gray-100 text-gray-500 px-1 py-0.5 rounded font-semibold">awaiting finance</span>
            : p.rag_stale && <span className="text-[7px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded font-semibold">due</span>}
        </span>
      </div>
      <div className={`text-[9px] truncate ${awaiting ? 'text-gray-400' : 'text-gray-500'}`}>{p.detail || ''}{p.client_name ? ` · ${p.client_name}` : ''}{p.po_number ? ` · PO ${p.po_number}` : ''}</div>
      <div className="flex items-center gap-2 mt-1 flex-wrap">
        {awaiting ? (
          <span className="text-[7px] px-1.5 py-0.5 rounded font-semibold bg-gray-100 text-gray-500">Delivered · awaiting payment</span>
        ) : (
          <>
            <span className={`text-[7px] px-1.5 py-0.5 rounded font-semibold ${ds.bg}`}>{ds.label}</span>
            {p.open_actions > 0 && <span className="text-[7px] px-1.5 py-0.5 rounded font-semibold bg-teal-100 text-teal-700" title="Open actions on this project">💬 {p.open_actions} action{p.open_actions === 1 ? '' : 's'}</span>}
            {p.overdue_milestones > 0 && <span className="text-[7px] text-red-600 font-semibold">{p.overdue_milestones} overdue</span>}
            {p.rag_age_days != null && <span className="text-[7px] text-gray-400">updated {p.rag_age_days}d ago</span>}
            {p.rag_age_days == null && <span className="text-[7px] text-gray-400">no update yet</span>}
          </>
        )}
      </div>
    </button>
  );
}

// ── Recent-update row ─────────────────────────────────────────────────────────
// Each project update is a full snapshot, so on its own it can't tell you what
// actually moved. We diff this snapshot against the previous one and show the
// changed fields; click to expand the full narrative for that update.
function UpdateHistoryRow({ h, prev }) {
  const [open, setOpen] = useState(false);
  const norm = (v) => (v == null ? '' : String(v).trim());

  const changes = [];
  if (!prev) {
    changes.push('First update');
  } else {
    if (norm(h.delivery_status) !== norm(prev.delivery_status))
      changes.push(`Delivery status → ${dsMeta(h.delivery_status).label}`);
    if (norm(h.overall_rag) !== norm(prev.overall_rag))
      changes.push(`Overall RAG → ${h.overall_rag || '—'}`);
    if (norm(h.recent_progress) !== norm(prev.recent_progress)) changes.push('Recent progress');
    if (norm(h.next_period) !== norm(prev.next_period)) changes.push('Next steps');
  }

  const fields = [
    ['Recent progress', h.recent_progress],
    ['Next steps', h.next_period],
  ].filter(([, v]) => norm(v));

  return (
    <div className="border border-gray-100 rounded">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full text-left px-2.5 py-1.5 flex items-start gap-2 hover:bg-gray-50">
        {h.overall_rag && <span className="inline-block w-2 h-2 rounded-full mt-1 shrink-0" style={{ background: RAG_DOT[h.overall_rag] }} />}
        <div className="min-w-0 flex-1">
          <div className="text-[9px] text-gray-500">
            {h.week_commencing ? formatDate(h.week_commencing) : '—'} · {dsMeta(h.delivery_status).label} · {h.updated_by_name || ''}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {changes.length === 0
              ? <span className="text-[7px] text-gray-400 italic">Re-submitted — no field changes</span>
              : changes.map((c, i) => (
                  <span key={i} className="text-[7px] font-semibold bg-navy/5 text-navy border border-navy/10 px-1.5 py-0.5 rounded">{c}</span>
                ))}
          </div>
        </div>
        <span className="text-[7px] text-gray-400 shrink-0 mt-1">{open ? 'hide' : fields.length ? 'detail' : ''}</span>
      </button>
      {open && fields.length > 0 && (
        <div className="px-2.5 pb-2 pt-0.5 space-y-1.5 border-t border-gray-50">
          {fields.map(([label, val]) => (
            <div key={label}>
              <div className="text-[7px] font-bold text-gray-400 uppercase">{label}</div>
              <div className="text-[9px] text-gray-700 whitespace-pre-wrap break-words">{val}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
